import * as React from "react";
import ReactDOM from "react-dom";
import { darken } from "polished";
import styled, { css } from "styled-components";

import { useEventListener } from "src/library/hooks";

import { Text } from "../Text";
import { Icon } from "../Icon";
import { Icons } from "../Icon/types";
import Button from "./Button";
import { Tooltip } from "../Tooltip";
import { HtmlBody } from "../Notes";

export interface IActionButtonProps {
  title: string;
  items: IAction[];
  loading?: boolean;
  disabled?: boolean;
  firstActionClickable?: boolean;
  buttonType?: "primary" | "secondary";
}
export interface IAction {
  title: string | JSX.Element;
  action: () => void;
  icon?: Icons
  newTabPath?: string;
  disabled?: boolean;
  toolTip?: {
    id: string;
    tip: string;
  }
}

const ActionsStyled = styled.div<{ buttonType: IActionButtonProps["buttonType"]}>`
  ${({ theme, buttonType }) => css`
    transition: ${theme.transition.quicker};
    display: inline-block;
    position: relative;
    button {
      position: relative;
      margin: 0;
      padding: 0 15px 0 15px;
      width: 100%;
      text-align: center;
      text-transform: capitalize;
      justify-content: space-between;

      &.has-icon {
        padding: 0 8px 0 15px;
      }

      .icon {
        position: relative;
        line-height: 35px;
        display: flex;
        padding-left: ${theme.spacing.padding.sm};
      }
    }
    ul {
      transition: ${theme.transition.quicker};
      opacity: 0;
      position: absolute;
      z-index: 2;
      width: 0;
      min-width: 100%;
      height: 0;
      font-size: ${theme.fonts.sizes.h4};
      text-align: left;
      background-color: ${theme.colors.white};
      border-radius: ${theme.borderRadius};
      box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.2);
      overflow: hidden;
      li {
        transition: ${theme.transition.quickest};
        display: block;
        padding: 10px 15px;
        border-bottom: 2px solid ${theme.colors.primaryLight};
        text-decoration: none;
        cursor: pointer;
        &:last-child {
          border-bottom: none;
        }
        &:hover {
          background-color: ${darken(0.1, theme.colors.lightestGrey)};
        }
      }
    }
    &.active {
      button {
        z-index: 3;
        border-radius: ${theme.borderRadius} ${theme.borderRadius} 0 0;
      }
    }

    .first-clickable-wrapper {
      display: flex;

      .first-clickable-dropdown {
        background: ${buttonType === "primary" ? theme.colors.primaryLight : theme.colors.white};
        display: flex;
        align-items: center;
        justify-content: center;
        border-top-right-radius: ${theme.borderRadius};
        border-bottom-right-radius: ${theme.borderRadius};
        border: ${buttonType === "primary" ? "0" : "2"}px solid ${buttonType === "primary" ? theme.colors.white : theme.colors.primaryLight};
        border-left: 1px solid ${buttonType === "primary" ? theme.colors.white : theme.colors.primaryLight};
        width: 30px;

        &:hover {
          cursor: pointer;
          transition: ${theme.transition.quicker};
          opacity: 0.8;

          ${buttonType !== "primary" ? css`
            background: ${theme.colors.primaryLight};
          ` : css``}
        }
      }

      .first-clickable-button {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  `}
`;

const StyledDropDown = styled.ul<{ x: number; y: number }>`
  ${({
    theme, x, y
  }) => css`
    /* transition: ${theme.transition.quicker}; */
    width: auto;
    position: absolute;
    z-index: 100;
    font-size: ${theme.fonts.sizes.p};
    text-align: left;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius};
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.2);
    min-width: 200px;
    top: ${y}px;
    left: ${x}px;
    transform: translateX(-100%);
    display: none;
    &.active {
      display: block;
    }
    
    li {
      transition: ${theme.transition.quickest};
      display: flex;
      
      border-bottom: 2px solid ${theme.colors.primaryLight};
      text-decoration: none;
      cursor: pointer;
      &:last-child {
        border-bottom: none;
      }

      .inner {
        padding: 10px;
        display:flex;
        flex-direction: row;
        flex-grow: 1;
        align-items center;
        svg {
          margin-left: ${theme.spacing.margins.xs};
          margin-right: ${theme.spacing.margins.xs};
        }

      }
      

      .new-tab {
        transition: ${theme.transition.quickest};
        opacity: 0;
      }

      &:hover {
        .new-tab {
          opacity: 1;
        }
      }

      .inner:hover,.new-tab:hover {
        background-color: ${darken(0.1, theme.colors.lightestGrey)};
      }

      .new-tab {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
      }

      .note-body, .note-body p {
        color: ${theme.colors.white};
      }
    }

    .disabled-li {
      cursor: not-allowed;

      .inner {
        background-color: ${theme.colors.ghost};
  
      }

      .inner span {
        color: #fff;
      }
    }

  `}
`;

const ActionButton: React.ReactFCWithChildren<IActionButtonProps> = ({
  title, items, disabled, loading, firstActionClickable, buttonType = "primary"
}) => {
  const [ isActive, setIsActive ] = React.useState(false);

  const [ drodownPosition, setDropdownPosition ] = React.useState({
    x: 0,
    y: 0
  });

  // Call the given action if not disabled. Stop propagation if disabled so menu stays open.
  const handleSelect = React.useCallback((action: () => void, isDisabled?: boolean, event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isDisabled) {
      event?.stopPropagation();

      return;
    }

    action();
    setIsActive(false);
  }, []);

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLUListElement>(null);

  const handler = React.useCallback(() => {
    if (isActive) {
      if (wrapperRef.current) {
        if (dropdownRef.current) {
          const bounds = wrapperRef.current.getBoundingClientRect();

          setDropdownPosition({
            y: bounds.top + bounds.height,
            x: bounds.right
          });
        }
      }
    }
  }, [
    isActive,
    wrapperRef.current,
    dropdownRef.current
  ]);

  const clickHandler = React.useCallback(
    (event: MouseEvent) => {
      if (isActive) {
        if (wrapperRef.current && wrapperRef.current.contains && !wrapperRef.current.contains(event.target as Node)) {
          setIsActive(false);
        }
      }
    },
    [ isActive, wrapperRef.current ]
  );

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));
  // useEventListener("scroll", handler, document.getElementsByClassName("rt-table")[0]);
  useEventListener("click", clickHandler, document);

  React.useEffect(() => {
    if (isActive) {
      if (wrapperRef.current) {
        if (dropdownRef.current) {
          const bounds = wrapperRef.current.getBoundingClientRect();

          setDropdownPosition({
            y: bounds.top + bounds.height,
            x: bounds.right
          });
        }
      }
    }
  }, [ isActive ]);

  if (items.length === 0) {
    if (loading === undefined || loading === true) {
      return (
        <ActionsStyled
          className={`action-button-container ${isActive ? "active" : "inactive"}`}
          buttonType={buttonType}
        >
          <Button
            loading
            $buttonType={buttonType}
            onClick={() => null}
          >
            <></>
          </Button>
        </ActionsStyled>
      );
    } else {
      return (
        <Button
          disabled
          $buttonType={buttonType}
        >
          <>
            <span className="text">
              {title}
            </span>
          </>
        </Button>
      );
    }
  }

  if (items.length === 1) {
    return (
      <ActionsStyled
        className={`action-button-container ${isActive ? "active" : "inactive"}`}
        buttonType={buttonType}
      >
        <Button
          newTabPath={items[ 0 ].newTabPath}
          loading={loading}
          disabled={items[ 0 ].disabled || disabled}
          $buttonType={buttonType}
          onClick={() => handleSelect(items[ 0 ].action)}
          icon={items[ 0 ].icon}
          iconPosition="right"
        >
          <>
            <span className="text">
              {items[ 0 ].title}
            </span>
          </>
        </Button>
      </ActionsStyled>
    );
  }

  return (
    <>
      <ActionsStyled
        className={`action-button-container ${isActive ? "active" : "inactive"}`}
        ref={wrapperRef}
        buttonType={buttonType}
      >
        {firstActionClickable ? (
          <div className="first-clickable-wrapper">
            <Button
              loading={loading}
              className="first-clickable-button"
              newTabPath={items[ 0 ].newTabPath}
              disabled={items[ 0 ].disabled}
              $buttonType={buttonType}
              onClick={() => handleSelect(items[ 0 ].action)}
              icon={items[ 0 ].icon}
            >
              <>
                <span className="text">
                  {items[ 0 ].title}
                </span>
              </>
            </Button>

            <div
              className="first-clickable-dropdown"
              onClick={() => setIsActive(isActive => !isActive)}
            >
              <i className="icon">
                <Icon
                  icon={isActive ? Icons.CHEVRONUP : Icons.CHEVRONDOWN}
                  $fillColor={buttonType === "primary" ? "white" : "primaryLight"}
                  $hoverColor={buttonType === "primary" ? undefined : "white"}
                  size={24}
                />
              </i>
            </div>
          </div>
        ) : (
          <Button
            disabled={disabled}
            $buttonType={buttonType}
            className="has-icon"
            loading={loading}
            onClick={() => {
              setIsActive(isActive => !isActive);
            }}
          >
            <>
              <span className="text">
                {title}
              </span>

              <i className="icon">
                <Icon
                  icon={isActive ? Icons.CHEVRONUP : Icons.CHEVRONDOWN}
                  $fillColor="white"
                  size={24}
                />
              </i>
            </>
          </Button>
        )}
      </ActionsStyled>

      {ReactDOM.createPortal(
        <StyledDropDown
          {...drodownPosition}
          ref={dropdownRef}
          className={isActive ? "active" : ""}
        >
          {items.map((item, i) => {
            // Dont render the first item as its already the main action
            if (firstActionClickable && i === 0) {
              return null;
            }

            return (
              <li
                key={i}
                className={`${item.disabled ? "disabled-li" : ""}`}
              >
                <>
                  {item.toolTip && (
                    <Tooltip
                      id={`action-option-tooltip-${item.toolTip.id}`}
                      type="dark"
                      place="left"
                      getContent={() => (
                        <HtmlBody body={item.toolTip?.tip || ""} />
                      )}
                    />
                  )}

                  <div
                    data-tip
                    data-for={`action-option-tooltip-${item.toolTip?.id}`}
                    className="inner button"
                    onClick={e => handleSelect(item.action, item.disabled, e)}
                    data-cypress={item.title}
                  >
                    {item.icon ? (
                      <Icon
                        size={20}
                        icon={item.icon}
                      />
                    ) : null}

                    <Text>
                      {item.title}
                    </Text>

                  </div>
                </>

                {item.newTabPath && !item.disabled && (
                  <div
                    className="new-tab"
                    onClick={() => {
                      window.open(item.newTabPath, "blank");
                    }}
                    title="Open in new tab"
                  >
                    <Icon
                      size={16}
                      icon={Icons.OPENNEW}
                      className="icon"
                    />
                  </div>
                )}
              </li>
            );
          })}
        </StyledDropDown>,
        document.body
      )}
    </>
  );
};

export default ActionButton;
